import { logger } from "@repo/logs";
import { serializeUnknownForLog } from "./serialize-unknown-for-log";

/** Structured context for an invariant violation (entity ids, what was missing). */
export type InvariantContext = Record<string, unknown>;

/** Thrown by {@link invariant} when a value the code relies on is missing. */
export class InvariantError extends Error {
	override readonly name = "InvariantError";
	readonly context: InvariantContext;

	constructor(message: string, context: InvariantContext) {
		super(message);
		this.context = context;
	}
}

/**
 * Fail closed when an invariant does not hold: log one structured line through
 * `@repo/logs` (`kind: "invariant.violated"`) and throw {@link InvariantError}.
 * Uses a truthiness check and narrows `condition` for TypeScript.
 *
 * Single source of truth for invariant checks; do not add parallel helpers.
 */
export function invariant(
	condition: unknown,
	message: string,
	context: InvariantContext = {},
): asserts condition {
	if (condition) {
		return;
	}
	logger.error(
		JSON.stringify({
			kind: "invariant.violated",
			message,
			context: serializeInvariantContext(context),
		}),
	);
	throw new InvariantError(message, context);
}

function serializeInvariantContext(context: InvariantContext): unknown {
	return JSON.parse(
		JSON.stringify(serializeUnknownForLog(context), (_key, value) =>
			typeof value === "bigint" ? value.toString() : value,
		),
	);
}
